import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
// Redux actions
import {
  registerSupplierAction,
  setPlantInfoAction,
} from '@/js/reduxstore/actions/authActions';
// Components
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

import createApiClient from '@/api/axiosInstance';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';

// Assets
import defaultImage from '@/js/images/default-machine.svg';
import {
  Check,
  ChevronLeft,
  CircleCheckBig,
  Edit2,
  Loader2,
  Plus,
  ShieldAlert,
} from 'lucide-react';

// Config
import AddMachinePopupForm from './AddMachinePopupForm';

const plantInformationSchema = z.object({
  plant_name: z.string().min(1, 'Plant name is required'),
  area_of_plant: z
    .number()
    .positive('Number of people must be a positive integer'),
  location: z.string().min(1, 'Location is required'),
  people_working: z
    .number()
    .int()
    .positive('Number of people must be a positive integer'),
  type_of_plant: z.string().min(1, 'Type of plant is required'),
  number_of_machines: z
    .number()
    .min(1, 'Information about machines is required'),
  // number_of_washrooms: z
  //   .number()
  //   .int()
  //   .nonnegative('Number of washrooms must be a non-negative integer'),
  // number_of_staff_rooms: z
  //   .number()
  //   .int()
  //   .nonnegative('Number of staff rooms must be a non-negative integer'),
});

const AddPlantForm = ({ setSectionId, identity = null }) => {
  // Identity is the plant_id (if editing) if passed, else null

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, []);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reduxData = useSelector(
    (state) => state?.authReducer?.registrationData,
  );
  const apiClient = createApiClient(navigate, dispatch);
  console.log(identity, 'identity');
  const token = useSelector((state) => state.authReducer.token);
  const [processing, setProcessing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [saveSuccess, setSaveSuccess] = React.useState(false);

  const [localPlantInfo, setLocalPlantInfo] = useState(
    reduxData?.plant_info || [],
  );
  const [localMachineInfo, setLocalMachineInfo] = useState(
    reduxData?.machining_info || [],
  );

  useEffect(() => {
    if (reduxData?.plant_info) {
      setLocalPlantInfo(reduxData.plant_info);
    }
    if (reduxData?.machining_info) {
      setLocalMachineInfo(reduxData.machining_info);
    }
    console.log(localPlantInfo);
  }, [reduxData]);

  // If we are editing a plant, get the plant info
  // const toEditPlant =
  //   localPlantInfo.filter((plant) => plant?.plant_id === identity)[0] || null;
  const [toEditPlant, setToEditPlant] = useState(
    localPlantInfo?.filter((plant) => plant?.plant_id === identity)[0] || null,
  );

  // If the current plant has any machines bool:
  const hasMachines =
    identity === null
      ? false
      : localMachineInfo?.filter((machine) => machine.plant_id === identity)
          .length > 0;
  console.log(
    'hasMachines',
    hasMachines,
    localMachineInfo?.filter((machine) => machine.plant_id === identity),
  );

  // Update local state when Redux data changes
  // useEffect(() => {
  //   const updateMachineInfo = () => {
  //     if (reduxData?.machining_info) {
  //       const uniqueMachines = reduxData.machining_info.filter(
  //         (machine) => machine.plant_id === identity,
  //       );

  //       // Force a state update even if it's the first machine
  //       setLocalMachineInfo((prevMachines) => {
  //         const updatedMachines = [
  //           ...prevMachines.filter((m) => m.plant_id !== identity),
  //           ...uniqueMachines,
  //         ];
  //         return updatedMachines;
  //       });
  //     }
  //   };

  //   updateMachineInfo();
  // }, [reduxData?.machining_info, identity, reduxData]);

  const form = useForm({
    resolver: zodResolver(plantInformationSchema),
    defaultValues: {
      plant_name: toEditPlant?.plant_name || '',
      location: toEditPlant?.location || '',
      type_of_plant: toEditPlant?.type_of_plant || '',

      area_of_plant: toEditPlant?.area_of_plant
        ? Number(toEditPlant?.area_of_plant)
        : '',
      people_working: toEditPlant?.people_working
        ? Number(toEditPlant?.people_working)
        : '',
      number_of_machines: toEditPlant?.number_of_machines
        ? Number(toEditPlant?.number_of_machines)
        : '',
    },
  });

  const [hasSavedPlantInfo, setHasSavedPlantInfo] = useState(false);

  // Fix: If edit is active, then the Add Machine button should be enabled
  useEffect(() => {
    if (identity !== null) {
      setHasSavedPlantInfo(true);
    }
  }, []);

  const onSubmit = async (data) => {
    setProcessing(true);
    setSaveSuccess(false);
    const plantInformation = {
      plant_name: data.plant_name.trim(),
      area_of_plant: parseFloat(data.area_of_plant),
      type_of_plant: data.type_of_plant.trim(),
      people_working: Math.round(data.people_working),
      location: data.location.trim(),
      number_of_machines: Math.round(data.number_of_machines),
    };

    try {
      const isEdit = identity !== null;

      const endpoint = isEdit
        ? `/api/manufacturer-profile/plant-info/${identity}`
        : '/api/manufacturer-profile/plant-info';
      const method = isEdit ? 'put' : 'post';

      const response = await apiClient[method](endpoint, plantInformation);
      const plantData = response.data;

      // For edit: use existing identity, for new: use ID from response
      const reduxPlantData = {
        ...plantInformation,
        plant_id: isEdit ? identity : plantData.data.plant_id,
      };

      console.log('Plant Information:', plantInformation);

      setSaveSuccess(true);
      // Reset success state after 2 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 1000);

      dispatch(setPlantInfoAction(reduxPlantData, isEdit));
      setLocalMachineInfo(reduxData.machining_info);
      setHasSavedPlantInfo(true); // Flag to enable adding machines
      setToEditPlant(reduxPlantData);

      console.log('toEditPlant after update', toEditPlant);
    } catch (error) {
      console.error(error);
      setError(
        error.response?.data?.error_message ||
          'Failed to submit plant information',
      );
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="w-full bg-white p-8 -mt-6 px-16 reltive rounded-md font-roboto text-gray-600 ">
      {/* <h2 className="text-3xl font-semibold text-black text-center mb-4">
        Plant Information
      </h2> */}
      {/* <p className="text-center text-[#637381] mb-8 text-base">
        Please add specific information to impress our buyers with your capacity and capabilities
      </p> */}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="mt-8 space-y-4">
          <div className="flex space-x-4">
            <FormField
              control={form.control}
              name="plant_name"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Name Of Plant
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="eg. DGI Power Plant"
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="type_of_plant"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Plant Type
                  </FormLabel>

                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger
                        className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple  ${field.value ? 'text-black' : 'text-gray-500'} `}
                      >
                        <SelectValue placeholder="Select from below" />
                      </SelectTrigger>
                    </FormControl>

                    <SelectContent>
                      <SelectItem value="Manufacturing">
                        Manufacturing
                      </SelectItem>
                      <SelectItem value="Warehouse">Warehouse</SelectItem>
                      <SelectItem value="QA">QA</SelectItem>
                      <SelectItem value="R&D">R&D</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex space-x-4">
            <FormField
              control={form.control}
              name="area_of_plant"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Area of Plant (in sq. ft)
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min="0"
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      step="0.1"
                      placeholder="250"
                      onChange={(e) =>
                        field.onChange(parseFloat(e.target.value) || '')
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="people_working"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    People Working
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min="0"
                      placeholder="e.g. 515"
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value, 10) || '')
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex space-x-4">
            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Location
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      placeholder="eg. 5th Avenue, San Francisco, CA, USA"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="number_of_machines"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Machines In Plant
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min="0"
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      placeholder="Number machines in this plant"
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value, 10) || '')
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className="flex space-x-4">
            {/* <FormField
              control={form.control}
              name="number_of_washrooms"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Washrooms In Plant
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min="0"
                      placeholder="e.g. 10"
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value, 10) || '')
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}

            {/* <FormField
              control={form.control}
              name="number_of_staff_rooms"
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormLabel className="text-foreground font-semibold text-gray-700">
                    Staff Rooms In Plant
                  </FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="number"
                      min="0"
                      className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 text-black focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple"
                      placeholder="e.g. 40"
                      onChange={(e) =>
                        field.onChange(parseInt(e.target.value, 10) || '')
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            /> */}
          </div>

          {/* <Button
            type="button"
            variant="ghost"
            onClick={() => setSectionId(7)}
            className="
             absolute   top-0 right-0 -translate-x-14 -translate-y-10 flex
                items-center justify-center text-darkPurple hover:text-darkerPurple ease-in duration-300  hover:bg-gray-100 rounded-lg"
          >
            <ChevronLeft className="h-6 w-6" />
            back
          </Button> */}

          <div className="flex pt-3 justify-between mt-10 text-lg">
            <Button
              type="button"
              variant="outline"
              className="px-4 py-4 text-black/95 border-black/40 w-28 hover:bg-slate-200"
              onClick={() => setSectionId(7)}
            >
              Back
            </Button>
            <Button
              type="submit"
              className={`px-4 py-4 w-28 ease-out duration-300 ${
                saveSuccess
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-darkPurple hover:bg-darkerPurple'
              }`}
              disabled={processing}
            >
              {processing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="font-roboto font-semibold text-base">
                    Saving...
                  </span>
                </>
              ) : saveSuccess ? (
                <>
                  <CircleCheckBig className=" h-4 w-4" />
                  <span className="font-roboto font-semibold text-base">
                    Saved
                  </span>
                </>
              ) : (
                <span className="font-roboto font-semibold text-base">
                  Save
                </span>
              )}
            </Button>
          </div>
          {/* Footer Message */}
          <div className="flex flex-row items-center justify-center space-x-2 ">
            {/* conditional renderign if no error display this if error dispaly error instead */}
            {error ? (
              <>
                <ShieldAlert className="h-3 w-3" />
                <span className="text-xs text-red-600">{error}</span>
              </>
            ) : (
              <>
                <span className="text-xs text-[#2CD673]"></span>
              </>
            )}
          </div>

          <Separator className="my-6" />
        </form>
      </Form>

      <div id="Machines" className="pt-5">
        <div className="w-full flex justify-between items-center">
          <h2 className="text-2xl font-semibold text-gray-600 text-center mb-4">
            {hasMachines
              ? `${
                  localPlantInfo?.filter(
                    (plant) => plant.plant_id === identity,
                  )[0]?.plant_name || 'Plant'
                } machines`
              : 'No Machines found'}
          </h2>
          <span>
            {hasSavedPlantInfo ? (
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    type="button"
                    className="mt-2 flex items-center px-5 py-2 text-sm bg-white border border-lightPurple text-lightPurple rounded-md hover:bg-lightPurple hover:text-white transition-colors"
                  >
                    <Plus size={15} className="mr-2" />
                    Add Machine
                  </Button>
                </DialogTrigger>
                <DialogContent className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border border-lightPurple bg-opacity-45 p-2 backdrop-filter text-black backdrop-blur-sm rounded-sm shadow-md">
                  <DialogTitle></DialogTitle>
                  <DialogDescription></DialogDescription>
                  <AddMachinePopupForm
                    setSectionId={setSectionId}
                    identity={toEditPlant.plant_id}
                    localMachineInfo={localMachineInfo}
                    setLocalMachineInfo={setLocalMachineInfo}
                    hasMachines={hasMachines}
                  />
                </DialogContent>
              </Dialog>
            ) : (
              <TooltipProvider>
                <Tooltip delayDuration={200}>
                  <TooltipTrigger asChild>
                    <span>
                      <Button
                        type="button"
                        // onClick={() => setSectionId(10)}
                        className="mt-2 flex px-5 py-2 text-sm bg-white border border-lightPurple text-lightPurple rounded-md hover:bg-lightPurple hover:text-white transition-colors"
                        disabled
                      >
                        <Plus size={15} className="mr-2" />
                        Add Machine
                      </Button>
                    </span>
                  </TooltipTrigger>

                  <TooltipContent className="bg-gray-400 border border-lightPurple bg-opacity-45 p-2 backdrop-filter text-black backdrop-blur-sm rounded-sm shadow-md translate-y-4 translate-x-16">
                    <p className="text-center text-sm text-gray-600">
                      Save Plant first
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </span>
        </div>
        {/* If No Machines are present */}
        {(!hasMachines ||
          localMachineInfo?.filter((machine) => machine.plant_id === identity)
            .length === 0) && (
          <div className="w-full flex justify-center items-center p-8">
            <div className="text-center">
              <div className="text-lightPurple/60 mb-3">
                <svg
                  className="w-16 h-16 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900">
                No machines added
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by adding your first machine
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-4 gap-5 pt-4 mt-4 max-w-6xl">
          {localMachineInfo
            ?.filter((machine) => machine.plant_id === identity)
            .map((machine) => (
              <div
                key={`${machine.plant_id}-${machine.machine_id}`}
                className=" flex flex-col items-start bg-white border shadow-md border-lightPurple/30 rounded-md p-5 w-full max-w-md md:max-w-lg lg:max-w-xl"
              >
                {/* Image */}
                <img
                  src={defaultImage}
                  alt="default Plant"
                  className="max-w-96 bg-gray-100 mb-3 p-3 rounded-md max-h-56  w-full object-cover text-center"
                />

                {/* Machine Name (No Truncation, Auto-wrap) */}
                <h2 className="text-lg md:text-xl font-semibold text-darkerPurple mb-1">
                  {machine.machine_name}
                </h2>

                {/* Plant Name (Truncated if too long) */}
                <p className="text-gray-600 text-xs md:text-sm min-h-5 truncate w-full">
                  {machine.plant_name && machine.plant_name.trim() !== '' && (
                    <>
                      <span className="font-medium">Plant: </span>
                      <span>{machine.plant_name}</span>
                    </>
                  )}
                </p>

                {/* Edit Button & Dialog */}
                <Dialog className="">
                  <DialogTrigger asChild>
                    <Button
                      type="button"
                      className="mt-2 flex w-full h-8 items-center px-5 py-2 text-sm bg-white border border-lightPurple text-lightPurple rounded-md hover:bg-lightPurple hover:text-white transition-colors group"
                    >
                      <Edit2
                        size={20}
                        className="transition-transform duration-200 group-hover:rotate-360 cursor-pointer"
                      />
                      Edit
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white border border-lightPurple bg-opacity-45 p-2 backdrop-filter text-black backdrop-blur-md rounded-sm shadow-md">
                    <DialogTitle className="text-lg font-semibold text-gray-600"></DialogTitle>
                    <DialogDescription></DialogDescription>
                    <AddMachinePopupForm
                      setSectionId={setSectionId}
                      identity={
                        localPlantInfo.filter(
                          (plant) => plant?.plant_id === identity,
                        )[0]?.plant_id
                      }
                      hasMachines={hasMachines}
                      machineId={machine.machine_id}
                    />
                  </DialogContent>
                </Dialog>
              </div>
            ))}
        </div>
        {/*  */}
      </div>
    </div>
  );
};

export default AddPlantForm;
